<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Categories } from '@/types'

const props = defineProps<{
  show: boolean
  categories: Categories
  selectedCategories: string[]
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  'select': [category: string]
}>()

const wheelRotation = ref(0)
const isSpinning = ref(false)
const selectedCategory = ref<string | null>(null)

const categoryPool = computed(() => {
  if (props.selectedCategories.length === 0) {
    return Object.keys(props.categories)
  }
  return props.selectedCategories
})

const wheelCategories = computed(() => {
  return categoryPool.value.map(key => ({
    key,
    name: key,
    emoji: props.categories[key]?.emoji || '❓'
  }))
})

const getSegmentPath = (index: number) => {
  const total = wheelCategories.value.length
  const angle = (2 * Math.PI) / total
  const startAngle = index * angle
  const endAngle = startAngle + angle

  const x1 = 100 + 80 * Math.cos(startAngle)
  const y1 = 100 + 80 * Math.sin(startAngle)
  const x2 = 100 + 80 * Math.cos(endAngle)
  const y2 = 100 + 80 * Math.sin(endAngle)

  const largeArcFlag = angle > Math.PI ? 1 : 0

  return `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2} Z`
}

const getSegmentColor = (index: number) => {
  const colors = ['#2DD4BF', '#14B8A6', '#0D9488', '#0F766E', '#134E4A', '#115E59', '#CCFBF1', '#99F6E4']
  return colors[index % colors.length]
}

const getLabelX = (index: number) => {
  const total = wheelCategories.value.length
  const angle = (2 * Math.PI) / total
  const midAngle = index * angle + angle / 2 - Math.PI / 2
  return 100 + 60 * Math.cos(midAngle)
}

const getLabelY = (index: number) => {
  const total = wheelCategories.value.length
  const angle = (2 * Math.PI) / total
  const midAngle = index * angle + angle / 2 - Math.PI / 2
  return 100 + 60 * Math.sin(midAngle)
}

const getLabelRotation = (index: number) => {
  const total = wheelCategories.value.length
  const angle = (2 * Math.PI) / total
  const midAngle = index * angle + angle / 2 - Math.PI / 2
  return (midAngle * 180 / Math.PI) + 90
}

const spinWheel = async () => {
  if (isSpinning.value) return

  isSpinning.value = true
  selectedCategory.value = null

  const spins = 5 + Math.random() * 3
  const total = wheelCategories.value.length
  const randomSegment = Math.floor(Math.random() * total)
  const segmentAngle = 360 / total
  const targetAngle = randomSegment * segmentAngle + segmentAngle / 2

  const currentRotation = wheelRotation.value % 360
  const finalRotation = currentRotation + (spins * 360) + (360 - targetAngle)

  wheelRotation.value = finalRotation

  await new Promise(resolve => setTimeout(resolve, 4000))

  const normalizedRotation = (360 - (finalRotation % 360) + segmentAngle / 2) % 360
  const winningIndex = Math.floor(normalizedRotation / segmentAngle) % total

  selectedCategory.value = wheelCategories.value[winningIndex].key
  isSpinning.value = false

  triggerConfetti()
}

const triggerConfetti = () => {
  if (typeof window !== 'undefined' && (window as any).confetti) {
    (window as any).confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    })
  }
}

const confirmCategory = () => {
  if (selectedCategory.value) {
    emit('select', selectedCategory.value)
    emit('update:show', false)
    selectedCategory.value = null
  }
}

const close = () => {
  if (!isSpinning.value) {
    emit('update:show', false)
    selectedCategory.value = null
  }
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-50">
    <div class="glass rounded-3xl p-6 max-w-md w-full animate-fade-in">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">🎰 Ruleta de Categorías</h2>
        <button 
          @click="close" 
          :disabled="isSpinning"
          class="text-white text-2xl hover:scale-110 transition-transform disabled:opacity-50"
        >
          ✕
        </button>
      </div>

      <p class="text-center text-teal-200 mb-4">
        {{ isSpinning ? '🎰 Girando...' : selectedCategory ? '¡Categoría seleccionada!' : 'Toca 🎯 para girar' }}
      </p>

      <div class="wheel-container">
        <svg 
          viewBox="0 0 200 200" 
          class="wheel"
          :style="{ transform: `rotate(${wheelRotation}deg)`, transition: isSpinning ? 'transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none' }"
        >
          <g v-for="(cat, index) in wheelCategories" :key="cat.key">
            <path 
              :d="getSegmentPath(index)" 
              :fill="getSegmentColor(index)"
              stroke="#fff"
              stroke-width="2"
            />
            <text
              :x="getLabelX(index)"
              :y="getLabelY(index)"
              :transform="`rotate(${getLabelRotation(index)}, ${getLabelX(index)}, ${getLabelY(index)})`"
              text-anchor="middle"
              dominant-baseline="middle"
              class="wheel-label"
            >
              {{ cat.emoji }}
            </text>
          </g>
        </svg>
        <button 
          @click="spinWheel" 
          class="wheel-center"
          :disabled="isSpinning"
        >
          {{ isSpinning ? '🎰' : '🎯' }}
        </button>
      </div>

      <div v-if="selectedCategory" class="glass rounded-xl p-4 mb-4 text-center pop-reveal">
        <p class="text-sm text-teal-300">Categoría seleccionada:</p>
        <p class="text-2xl font-bold text-white">
          {{ categories[selectedCategory]?.emoji }} {{ selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1) }}
        </p>
      </div>

      <div class="space-y-2">
        <button
          @click="confirmCategory"
          :disabled="!selectedCategory || isSpinning"
          class="w-full py-3 px-6 bg-accent text-dark font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirmar
        </button>
        <button
          @click="close"
          :disabled="isSpinning"
          class="w-full py-3 px-6 bg-white/20 text-white font-bold rounded-xl hover:bg-white/30 transition-colors disabled:opacity-50"
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wheel-container {
  position: relative;
  width: 300px;
  height: 300px;
  margin: 0 auto 2rem;
}

.wheel {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));
}

.wheel-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2DD4BF 0%, #0D9488 100%);
  border: 4px solid #fff;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  font-size: 2rem;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wheel-center:hover:not(:disabled) {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.wheel-center:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.wheel-label {
  font-size: 16px;
  font-weight: bold;
  fill: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.pop-reveal {
  animation: popReveal 0.5s ease-out;
}

@keyframes popReveal {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.glass {
  background: rgba(13, 148, 136, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(45, 212, 191, 0.3);
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>